import React from 'react';
import {
  ReportColumn,
  DEFAULT_SHOW,
  ACTION_COLUMN,
  ReportFilter,
} from 'components/modules/report/report-types';
import { Model } from 'util/backendapi/models/api.interfaces';
import { Trans, Plural, t } from '@lingui/macro';
import PageStandard from 'components/modules/pagestandard/pagestandard';
import { ReportTable } from 'components/modules/report/table/ReportTable';
import { ReportFiltersBlock } from 'components/modules/report/filter/ReportFiltersBlock';
import { ReportStateProps } from 'hooks/use-report-state';
import ActionBlock from 'components/base/actionblock/actionblock';
import { EditPerformanceIndicatorModal } from './EditPerformanceIndicatorModal';
import ButtonShowModal from 'components/base/modal/buttonshowmodal';
import { ButtonShowConfirmation } from 'components/base/confirmation/ButtonShowConfirmation';

export const PerformanceIndicatorListFilters: ReportFilter[] = [];

export const performanceIndicatorListColumns: (props: {
  refreshList: () => void;
  retirePerformanceIndicator: (id: number) => Promise<any>;
}) => ReportColumn<Model.ReportsPerformanceIndicator>[] = ({
  refreshList,
  retirePerformanceIndicator,
}) => [
  {
    label: <Trans>Name</Trans>,
    name: 'name',
    visibility: DEFAULT_SHOW,
  },
  {
    ...ACTION_COLUMN,
    additionalFields: ['id', 'name'],
    accessor: ({ id, name }) => (
      <>
        <ButtonShowModal
          className="btn-link-panel"
          name="edit-performance-indicator"
          modalContent={({ hideModal }) => (
            <EditPerformanceIndicatorModal
              performanceIndicatorId={id}
              onAfterSubmit={() => {
                hideModal();
                refreshList();
              }}
            />
          )}
          iconType="icon-edit"
          iconOnly={true}
          title={t`Edit`}
        >
          <Trans>Edit</Trans>
        </ButtonShowModal>
        <ButtonShowConfirmation
          name="retire-performance-indicator"
          title={t`Retire performance indicator`}
          iconType="icon-circle-minus"
          className="btn-link-panel"
          content={
            <Trans>
              Are you sure you want to retire the <strong>{name}</strong>{' '}
              performance indicator? Once retired the performance indicator will
              not be available to assign to any observation points. This action
              is not reversible.
            </Trans>
          }
          okBtnText={<Trans>Yes, retire</Trans>}
          onConfirm={async function () {
            await retirePerformanceIndicator(id!);
            refreshList();
          }}
        />
      </>
    ),
  },
];

export interface PerformanceIndicatorListViewProps {
  reportProps: ReportStateProps<Model.ReportsPerformanceIndicator>;
  retirePerformanceIndicator: (id: number) => Promise<any>;
}

export const PerformanceIndicatorListView: React.FunctionComponent<PerformanceIndicatorListViewProps> =
  function (props) {
    const { reportProps, retirePerformanceIndicator } = props;
    const columns = performanceIndicatorListColumns({
      refreshList: reportProps.refreshList,
      retirePerformanceIndicator: retirePerformanceIndicator,
    });
    const filters = PerformanceIndicatorListFilters;
    return (
      <PageStandard
        name="performanceIndicator-list"
        header={
          reportProps.isExportMode ? (
            <Trans>Performance indicator report</Trans>
          ) : (
            <Trans>Maintain Performance Indicators</Trans>
          )
        }
      >
        <div className="page-content-header-filters-actions">
          <ActionBlock className="text-right">
            <ButtonShowModal
              name="create-performance-indicator"
              shortcut="CREATE_NEW"
              iconType="icon-plus"
              modalContent={({ hideModal }) => (
                <EditPerformanceIndicatorModal
                  onAfterSubmit={() => {
                    hideModal();
                    reportProps.refreshList();
                  }}
                />
              )}
            >
              <Trans>Create performance indicator</Trans>
            </ButtonShowModal>
          </ActionBlock>

          <ReportFiltersBlock
            filtersFrontend={filters}
            filtersBackend={
              reportProps.reportInfo && reportProps.reportInfo.filters
            }
            isExportMode={reportProps.isExportMode}
          />
        </div>
        <div className="filtered-table-wrapper">
          <ReportTable
            columns={columns}
            errorMessage={reportProps.errorMessage}
            filters={filters}
            isLoading={reportProps.isLoading}
            msgFilterCount={(count) => (
              <Plural
                value={count}
                one="Filtered to 1 performance indicator"
                other="Filtered to # performance indicators"
              />
            )}
            msgNoMatches={
              <Trans>
                No performance indicators match the selected filters.
              </Trans>
            }
            pagination={reportProps.pagination}
            records={reportProps.records}
            reportInfo={reportProps.reportInfo}
          />
        </div>
      </PageStandard>
    );
  };
